import { useEffect, useState } from 'react';
import { CheckCircle2, Mail, Key } from 'lucide-react';
import { useCreditsContext } from '../../contexts/CreditsContext';
import { trackEvent, Events } from '../../lib/posthog';

export function PricingModal() {
  const {
    showPricingModal,
    setShowPricingModal,
    credits,
    subscription,
    isSubscriber,
    purchaseCredits,
    redeemSuperAdmin,
  } = useCreditsContext();

  const [showAdminInput, setShowAdminInput] = useState(false);
  const [adminCode, setAdminCode] = useState('');
  const [adminError, setAdminError] = useState('');

  // Track when pricing modal is opened
  useEffect(() => {
    if (showPricingModal) {
      trackEvent(Events.PRICING_MODAL_OPENED);
    }
  }, [showPricingModal]);

  if (!showPricingModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-slate-900 rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-white/10">
        <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-4 sm:p-6 flex justify-between items-start gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold">Choose Your Plan</h2>
            <p className="text-slate-400 mt-1 text-sm sm:text-base">Get hyper-personalized swing analysis</p>
          </div>
          <button
            onClick={() => setShowPricingModal(false)}
            className="text-slate-400 hover:text-white text-2xl w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/5 flex-shrink-0"
          >
            ×
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {isSubscriber ? (
            /* Subscription Management View */
            <div className="text-center">
              <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-2 border-emerald-500/30 p-8 mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 mb-4">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Active Subscription</h3>
                <p className="text-slate-400 mb-4">You're subscribed to SwingSlot Monthly</p>
                <div className="bg-white/5 rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-400">Status</span>
                    <span className="text-emerald-400 font-medium capitalize">{subscription?.status || 'Active'}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-400">Current credits</span>
                    <span className="text-white font-bold">{credits}</span>
                  </div>
                  {subscription?.renewalDate && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Next renewal</span>
                      <span className="text-white">{new Date(subscription.renewalDate).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-slate-400 mb-4">
                  To manage your subscription (update payment, cancel, etc.), please contact support.
                </p>
                <a
                  href="mailto:support@swingslot.com?subject=Subscription%20Management"
                  className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition"
                >
                  <Mail className="h-4 w-4" />
                  Contact Support
                </a>
              </div>

              <div className="text-slate-500 text-sm">
                Need more credits before renewal? Purchase a one-time pack below.
              </div>

              {/* One-time credit packs for subscribers who need more */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-green-500/30 transition">
                  <h3 className="text-lg font-bold mb-1">5-Pack</h3>
                  <div className="text-2xl font-bold mb-1">$12.99</div>
                  <button
                    onClick={() => purchaseCredits('pack5')}
                    className="w-full rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 px-4 py-3 font-medium transition text-sm mt-2 min-h-[44px]"
                  >
                    Add 5 Credits
                  </button>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-emerald-500/30 transition">
                  <h3 className="text-lg font-bold mb-1">10-Pack</h3>
                  <div className="text-2xl font-bold mb-1">$22.99</div>
                  <button
                    onClick={() => purchaseCredits('pack10')}
                    className="w-full rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 px-4 py-3 font-medium transition text-sm mt-2 min-h-[44px]"
                  >
                    Add 10 Credits
                  </button>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-purple-500/30 transition">
                  <h3 className="text-lg font-bold mb-1">25-Pack</h3>
                  <div className="text-2xl font-bold mb-1">$49.99</div>
                  <button
                    onClick={() => purchaseCredits('pack25')}
                    className="w-full rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 px-4 py-3 font-medium transition text-sm mt-2 min-h-[44px]"
                  >
                    Add 25 Credits
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Non-subscriber purchase view */
            <>
              {/* Credit Packs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* 5 Pack */}
                <div className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-white/20 transition">
                  <h3 className="text-lg font-bold mb-1">5-Pack</h3>
                  <div className="text-3xl font-bold mb-1">$12.99</div>
                  <div className="text-sm text-slate-400 mb-4">$2.60 per analysis</div>
                  <button
                    onClick={() => purchaseCredits('pack5')}
                    className="w-full rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 px-6 py-3 font-bold transition"
                  >
                    Get 5 Credits
                  </button>
                </div>

                {/* 10 Pack */}
                <div className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-white/20 transition">
                  <h3 className="text-lg font-bold mb-1">10-Pack</h3>
                  <div className="text-3xl font-bold mb-1">$22.99</div>
                  <div className="text-sm text-slate-400 mb-4">$2.30 per analysis</div>
                  <button
                    onClick={() => purchaseCredits('pack10')}
                    className="w-full rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 px-6 py-3 font-bold transition"
                  >
                    Get 10 Credits
                  </button>
                </div>

                {/* 25 Pack */}
                <div className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-white/20 transition">
                  <h3 className="text-lg font-bold mb-1">25-Pack</h3>
                  <div className="text-3xl font-bold mb-1">$49.99</div>
                  <div className="text-sm text-slate-400 mb-4">$2.00 per analysis</div>
                  <button
                    onClick={() => purchaseCredits('pack25')}
                    className="w-full rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 px-6 py-3 font-bold transition"
                  >
                    Get 25 Credits
                  </button>
                </div>
              </div>

              {/* Monthly Subscription */}
              <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-2 border-emerald-500/30 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">Monthly Subscription</h3>
                    <p className="text-slate-400 text-sm sm:text-base">15 analyses per month • Recurring billing • Cancel anytime</p>
                    <ul className="mt-4 space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        <span>$2.00 per analysis (best value)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        <span>Priority support</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        <span>Early access to new features</span>
                      </li>
                    </ul>
                  </div>
                  <div className="sm:text-right">
                    <div className="text-3xl sm:text-4xl font-bold">$29.99<span className="text-lg sm:text-sm text-slate-400 font-normal">/month</span></div>
                  </div>
                </div>
                <button
                  onClick={() => purchaseCredits('monthly')}
                  className="w-full rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 px-6 py-4 font-bold text-black hover:scale-105 transition text-lg"
                >
                  Subscribe Now
                </button>
              </div>
            </>
          )}

          {/* Trust Badges */}
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-xs text-slate-500 mb-3">Secure payment powered by Stripe</p>
            <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-4 text-slate-600">
              <div className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-[10px] sm:text-xs">SSL Encrypted</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-[10px] sm:text-xs">Money-back guarantee</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-[10px] sm:text-xs">Cancel anytime</span>
              </div>
            </div>

            {/* Hidden Admin Code Input */}
            <div className="mt-4">
              {!showAdminInput ? (
                <button
                  onClick={() => setShowAdminInput(true)}
                  className="text-xs text-slate-600 hover:text-slate-400 transition"
                >
                  <Key className="h-3 w-3 inline mr-1" />
                  Have a code?
                </button>
              ) : (
                <div className="flex items-center justify-center gap-2 max-w-xs mx-auto">
                  <input
                    type="password"
                    value={adminCode}
                    onChange={(e) => {
                      setAdminCode(e.target.value);
                      setAdminError('');
                    }}
                    placeholder="Enter code"
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30 min-h-[44px]"
                    style={{ fontSize: '16px' }}
                  />
                  <button
                    onClick={async () => {
                      if (!adminCode.trim()) return;
                      const result = await redeemSuperAdmin(adminCode.trim());
                      if (result.success) {
                        setShowPricingModal(false);
                        setAdminCode('');
                        setShowAdminInput(false);
                        alert(result.message);
                      } else {
                        setAdminError(result.error);
                      }
                    }}
                    className="bg-white/10 hover:bg-white/15 border border-white/20 rounded-lg px-4 py-2 text-sm font-medium transition min-h-[44px]"
                  >
                    Redeem
                  </button>
                </div>
              )}
              {adminError && (
                <p className="text-red-400 text-xs mt-2">{adminError}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
